import { useState } from "react";
import ReactDatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Modal, Radio, Search } from "semantic-ui-react";

import { DATE_FORMATS_FNS, useCan } from "components";
import "components/CategoryLicenseModal/CategoryLicenseModal.scss";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { LOCATION_LICENSE_SUBPAGE } from "modules/LocationLicenseOverview";
import { useSelectedCategoryLicenseUpdateMutation } from "modules/LocationLicenseOverview/hooks";
import { filterLicenseData, findExactItemById, getLicenseCategoryName, getNextMonthlyInvoicingDate } from "modules/LocationLicenseOverview/utils";
import { formatCurrentUTCDate, formatDate, toDate } from "utils/dateHelperFunctions";
import { formatPrice } from "utils/formatPrice";

import type { ExactCategory, ExactCategoryLicense } from "models";
import type { LicenseType } from "modules/LocationLicenseOverview";
import type { Licenses } from "modules/LocationLicenseOverview/components";
import type { LicenseItem } from "modules/LocationLicenseOverview/queryKeys";
import type { SearchResultData } from "semantic-ui-react";

export enum INVOICING_OPTIONS {
  Monthly = 0,
  OnceOnDate,
  OnceInBillingCycle
}

type CategoryModalLicense = {
  price: number;
  search: string;
  item: LicenseItem | null;
  description: string | null;
  searchedItems: LicenseItem[];
  categoryLicenseID: number;
  isSearchDropdownOpen: boolean;
};

type CategoryLicenseModalProps = {
  items: LicenseItem[];
  isOpen: boolean;
  locationId: number;
  selectedTab: LOCATION_LICENSE_SUBPAGE;
  selectedLicense: LicenseType | null;
  isLoadingLicenses: boolean;
  licensesData?: Licenses;
  onClose: () => void;
  onUpdateExactLicenses?: (id: number) => void;
};

const defaultLicenseState: CategoryModalLicense = {
  search: "",
  searchedItems: [],
  item: null,
  description: null,
  price: 0,
  categoryLicenseID: 0,
  isSearchDropdownOpen: false
};

const FormHelper = createFormHelper<ExactCategoryLicense>();

//TODO: RHF was not properly implemented here, and we are dealing with old code, create a ticket to move this to RHF properly
export const CategoryLicenseModal = ({
  onClose,
  isOpen,
  selectedLicense,
  items,
  selectedTab,
  isLoadingLicenses,
  licensesData,
  locationId,
  onUpdateExactLicenses
}: CategoryLicenseModalProps) => {
  const { t } = useTranslation();

  const { mutateAsync: handleCategoryLicenseUpdate, isPending: isCategoryLicenseUpdatePending } = useSelectedCategoryLicenseUpdateMutation();
  const canUpdateLicenseCategory = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.ExactLicenseCategory);

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [hasValidationError, setHasValidationError] = useState(false);
  const [category, setCategory] = useState<ExactCategory | null>(null);
  const [customInvoicingDate, setCustomInvoicingDate] = useState<Date | null>(null);
  const [licenses, setLicenses] = useState<CategoryModalLicense[]>([defaultLicenseState]);
  const [selectedInvoicingOption, setSelectedInvoicingOption] = useState<INVOICING_OPTIONS | null>(INVOICING_OPTIONS.Monthly);

  const handleAddLicenseRow = () => setLicenses(prev => prev.map(license => ({ ...license, isSearchDropdownOpen: false })).concat({ ...defaultLicenseState }));

  const handleItemClick = (data: SearchResultData, index: number) => {
    const item: LicenseItem = data.result;

    setLicenses(prevLicenses => {
      const updatedLicenses = [...prevLicenses];
      updatedLicenses[index] = {
        ...updatedLicenses[index],
        item,
        search: `${item.Code} - ${item.Description}`,
        price: item.StandardSalesPrice || 0,
        searchedItems: [],
        isSearchDropdownOpen: false
      };
      return updatedLicenses;
    });
  };

  const handlePrice = (price: number, index: number) => setLicenses(licenses => licenses.with(index, { ...licenses[index], price, isSearchDropdownOpen: false }));

  const handleDescription = (value: string, index: number) => {
    setLicenses(prevLicenses => {
      const updatedLicenses = [...prevLicenses];
      updatedLicenses[index] = { ...updatedLicenses[index], description: value || "", isSearchDropdownOpen: false };
      return updatedLicenses;
    });
  };

  const deleteRow = (index: number) =>
    setLicenses(prevLicenses => prevLicenses.map(license => ({ ...license, isSearchDropdownOpen: false })).filter((_, i) => i !== index));

  const closeModal = () => {
    setLicenses([defaultLicenseState]);
    setSelectedInvoicingOption(null);
    setCustomInvoicingDate(null);
    setCategory(null);
    setIsDatePickerOpen(false);
    setHasValidationError(false);
    onClose();
  };

  const getCurrentCategory = (categories?: ExactCategory[]) => {
    return categories?.find(
      cat =>
        cat.license_category_type === selectedLicense?.license_category_type &&
        cat.role_id === selectedLicense.role_id &&
        cat.dealer_location_id === selectedLicense.dealer_location_id
    );
  };

  const setInvoicingData = () => {
    if (selectedTab === LOCATION_LICENSE_SUBPAGE.MonthlyLicenses) {
      setSelectedInvoicingOption(INVOICING_OPTIONS.Monthly);
    } else {
      if (selectedLicense?.invoiceOn) {
        setCustomInvoicingDate(new Date(selectedLicense.invoiceOn));
        setSelectedInvoicingOption(INVOICING_OPTIONS.OnceOnDate);
      } else {
        setSelectedInvoicingOption(INVOICING_OPTIONS.OnceInBillingCycle);
      }
    }
  };

  const handleOpenModal = () => {
    if (!selectedLicense?.license_category_type) return;

    const currentCategory = getCurrentCategory(
      selectedTab === LOCATION_LICENSE_SUBPAGE.MonthlyLicenses ? licensesData?.monthlyCategories : licensesData?.oneTimeCategories
    );

    if (!currentCategory) return;

    setInvoicingData();
    setCategory(currentCategory);

    if (!currentCategory.licenses) return;

    const cls = currentCategory.licenses.map((categoryLicense: ExactCategoryLicense) => {
      const item = findExactItemById(items, categoryLicense.exact_item_id);
      return {
        price: categoryLicense.price || item?.StandardSalesPrice || 0,
        categoryLicenseID: categoryLicense.id,
        item: item || null,
        description: categoryLicense.description || "",
        search: item ? `${item.Code} - ${item.Description}` : "",
        searchedItems: [],
        isSearchDropdownOpen: false
      };
    });
    setLicenses(cls);
  };

  const handleSearch = (index: number, value: string) => {
    const filteredValues = filterLicenseData(items, value);
    setLicenses(prevLicenses =>
      prevLicenses.map((license, idx) => ({
        ...license,
        isSearchDropdownOpen: idx === index,
        search: idx === index ? value : license.search,
        searchedItems: idx === index ? filteredValues : license.searchedItems
      }))
    );
  };

  const validateRequest = () => {
    if (selectedInvoicingOption === null || licenses.some(license => !license.item || !license.price)) {
      setHasValidationError(true);
      return false;
    }

    setHasValidationError(false);
    return true;
  };

  const prepareSaveParams = () => {
    if (selectedInvoicingOption === null || !locationId || !category) return;

    const validLicenses: ExactCategoryLicense[] = [];
    licenses.forEach(license => {
      if (!license.price) return;
      const exactLicense: ExactCategoryLicense = {
        price: license.price,
        category_id: Number(category.id),
        description: license.description || "",
        exact_item_id: license.item?.ID || "",
        id: license.categoryLicenseID || 0,
        created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat)
      };
      validLicenses.push(exactLicense);
    });

    const getInvoiceOnValue = () => {
      if (selectedInvoicingOption === INVOICING_OPTIONS.Monthly) return null;
      if (selectedInvoicingOption === INVOICING_OPTIONS.OnceOnDate)
        return customInvoicingDate ? formatDate(customInvoicingDate, DATE_FORMATS_FNS.ISOFormatStartOfDayWithZ) : null;

      return formatDate(getNextMonthlyInvoicingDate(), DATE_FORMATS_FNS.ISOFormatStartOfDayWithZ);
    };

    const invoiceOn = getInvoiceOnValue();

    return {
      category: {
        ...category,
        dealer_location_id: locationId,
        licenses: validLicenses,
        ...(invoiceOn && { invoice_on: invoiceOn }),
        one_time: selectedInvoicingOption !== INVOICING_OPTIONS.Monthly
      }
    };
  };

  const handleSave = async () => {
    if (!canUpdateLicenseCategory) return;

    const isValid = validateRequest();
    if (!isValid) return;

    const params = prepareSaveParams();
    if (!params) return;

    await handleCategoryLicenseUpdate(params);
    if (onUpdateExactLicenses && selectedLicense?.categoryID) onUpdateExactLicenses(selectedLicense.categoryID);

    closeModal();
  };

  const handleChangeDate = (date: Date | null) => {
    if (!date) return;

    setCustomInvoicingDate(date);
    setSelectedInvoicingOption(INVOICING_OPTIONS.OnceOnDate);
    setIsDatePickerOpen(false);
  };

  const getModalTitle = () => {
    const categoryType = category?.license_category_type ? ` - ${getLicenseCategoryName(category.license_category_type, t)}` : "";
    const subPageTitle = selectedTab === LOCATION_LICENSE_SUBPAGE.MonthlyLicenses ? t("v8_monthly", "Monthly") : t("v8_once", "Once");
    return `${t("v8_link_license", "Link license")}${categoryType} - ${subPageTitle}`;
  };

  return (
    <Modal open={isOpen} size="large" onClose={closeModal} onMount={handleOpenModal} closeOnDimmerClick={false} className="CategoryLicenseModal">
      <Modal.Header>{getModalTitle()}</Modal.Header>
      <FormHelper.Form onSubmit={handleSave} formProps={{ loading: isLoadingLicenses }}>
        <div className="content">
          {licenses.map((license, index) => (
            <Form.Group key={`${license.categoryLicenseID - index}`}>
              <Form.Field className="dropdown-search" width={7} error={hasValidationError && !license.search} required>
                {index < 1 && <label className="big">{t("v8_license", "License")}</label>}
                <div className="search">
                  <Search
                    fluid={true}
                    placeholder={t("v8_search_for_license", "Search for license")}
                    icon={<Icon className="magnifying glass" />}
                    noResultsMessage={t("v8_no_results", "No results found")}
                    onSearchChange={(_e, { value }) => handleSearch(index, value || "")}
                    onResultSelect={(_e, data) => handleItemClick(data, index)}
                    resultRenderer={({ Code, Description }) => <p>{`${Code} - ${Description}`}</p>}
                    results={license.searchedItems}
                    value={license.search}
                    open={license.isSearchDropdownOpen}
                  />
                  <Button color="green" type="button" className="mt-14 rowButton" onClick={() => handleSearch(index, license.search)}>
                    {t("v8_search", "Search")}
                  </Button>
                </div>
              </Form.Field>
              <Form.Field width={6}>
                {index < 1 && <label>{t("v8_overwrite_name", "Overwrite name")}</label>}
                <FormHelper.Field.Input
                  inputProps={{
                    fluid: true,
                    value: license.description ?? undefined
                  }}
                  name="description"
                  onChange={e => handleDescription(e, index)}
                />
              </Form.Field>
              <Form.Field width={2} error={hasValidationError && !license.price} required>
                {index < 1 && <label>{t("v8_unit_price", "Unit price")}</label>}
                <FormHelper.Field.Input
                  inputProps={{
                    fluid: true,
                    value: formatPrice(license.price),
                    type: "text",
                    label: { basic: true, content: "€" },
                    labelPosition: "left"
                  }}
                  isPriceInput
                  name="price"
                  onChange={price => handlePrice(price ?? 0, index)}
                />
              </Form.Field>
              <Form.Field width={1}>
                <Button onClick={() => deleteRow(index)} type="button" className="wdLightGrey centerIcon greyBorder delete-license">
                  <Icon className={`trash red`} />
                </Button>
              </Form.Field>
            </Form.Group>
          ))}
          <Form.Group>
            <Form.Field width={16}>
              <Button icon labelPosition="left" color="green" type="button" onClick={handleAddLicenseRow} className="addLicenseButton">
                <Icon className="plus" />
                {t("v8_add_license", "Add license")}
              </Button>
            </Form.Field>
          </Form.Group>
          {selectedTab !== LOCATION_LICENSE_SUBPAGE.MonthlyLicenses && (
            <>
              <Form.Group>
                <Form.Field width={16} className="mt-20" error={hasValidationError && selectedInvoicingOption === null} required>
                  <label>{t("v8_billing_options", "Billing options")}</label>
                </Form.Field>
              </Form.Group>
              <Form.Group>
                <Form.Field className="radio-button" width={16}>
                  <label>{`${t("v8_once_on_date", "Once, on date")}: ${customInvoicingDate ? formatDate(customInvoicingDate, DATE_FORMATS_FNS.dateMonthYear) : ""}`}</label>
                  <ReactDatePicker
                    selected={customInvoicingDate ? toDate(customInvoicingDate) : new Date()}
                    onChange={handleChangeDate}
                    showMonthDropdown
                    showYearDropdown
                    minDate={toDate(new Date())}
                    dateFormat="dd-MM-yyyy"
                    onClickOutside={() => setIsDatePickerOpen(false)}
                    onCalendarClose={() => setIsDatePickerOpen(false)}
                    onCalendarOpen={() => setIsDatePickerOpen(true)}
                    withPortal
                    inline={false}
                    open={isDatePickerOpen}
                    customInput={
                      <Radio
                        name="billingOptions"
                        value={INVOICING_OPTIONS.OnceOnDate}
                        checked={selectedInvoicingOption === INVOICING_OPTIONS.OnceOnDate}
                        onClick={() => setIsDatePickerOpen(true)}
                      />
                    }
                  />
                </Form.Field>
              </Form.Group>
              <Form.Group>
                <Form.Field className="radio-button" width={16}>
                  <label>{t("v8_once_on_next_months_billing_date", "Once, on next month's billing date")}</label>
                  <Radio
                    name="billingOptions"
                    value={INVOICING_OPTIONS.OnceInBillingCycle}
                    checked={selectedInvoicingOption === INVOICING_OPTIONS.OnceInBillingCycle}
                    onChange={() => setSelectedInvoicingOption(INVOICING_OPTIONS.OnceInBillingCycle)}
                  />
                </Form.Field>
              </Form.Group>
            </>
          )}
        </div>
        <div className="actions">
          <div className="action-buttons">
            <Button className="light" onClick={closeModal}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Button>
            <Button color="green" type="submit" disabled={isCategoryLicenseUpdatePending || !canUpdateLicenseCategory} loading={isCategoryLicenseUpdatePending}>
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Button>
          </div>
        </div>
      </FormHelper.Form>
    </Modal>
  );
};
